/* ==========================================================================
   BEELINE HONEYS — Motion & interaction engine
   --------------------------------------------------------------------------
   Dependency-free ES module. Provides:

     • scroll reveal (IntersectionObserver) with automatic stagger
     • pointer-driven 3D tilt with specular sheen
     • magnetic buttons
     • scroll parallax for decorative layers
     • <bl-counter>      animated number count-up
     • <bl-quiz>         the "find your honey" recommender
     • <bl-tabs>         accessible tab switcher
     • <bl-copy>         copy-to-clipboard for contact details

   Every effect is skipped when the visitor prefers reduced motion, and every
   effect degrades to plain static content when JS is unavailable.
   ========================================================================== */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const prefersReducedMotion = () => reducedMotion.matches;

/* Marks the document so the reveal CSS can hide elements. Done from JS only,
   so a no-JS visitor always sees fully rendered content. */
if (!prefersReducedMotion()) {
  document.documentElement.classList.add('bl-reveal-ready');
}

/* --------------------------------------------------------------------------
   Scroll reveal
   -------------------------------------------------------------------------- */

const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('bl-in');
      revealObserver.unobserve(entry.target);
    });
  },
  { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
);

function initReveal(root = document) {
  if (prefersReducedMotion()) return;

  root.querySelectorAll('[data-bl-reveal]').forEach((el) => {
    if (el.dataset.blRevealBound) return;
    el.dataset.blRevealBound = 'true';
    revealObserver.observe(el);
  });

  /* Stagger: every direct child of a [data-bl-stagger] container gets an
     incremental delay, so a grid cascades instead of popping at once. */
  root.querySelectorAll('[data-bl-stagger]').forEach((group) => {
    if (group.dataset.blStaggerBound) return;
    group.dataset.blStaggerBound = 'true';

    const step = parseInt(group.dataset.blStagger, 10) || 90;
    const items = group.querySelectorAll(':scope > [data-bl-reveal], :scope > * > [data-bl-reveal]');

    items.forEach((item, index) => {
      /* Cap the cascade so a long grid never leaves the last card waiting. */
      item.style.setProperty('--bl-delay', `${Math.min(index * step, step * 8)}ms`);
    });
  });
}

/* --------------------------------------------------------------------------
   3D tilt
   -------------------------------------------------------------------------- */

function initTilt(root = document) {
  if (prefersReducedMotion()) return;
  /* Tilt is a pointer affordance — pointer-less devices skip it entirely. */
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  root.querySelectorAll('[data-bl-tilt]').forEach((el) => {
    if (el.dataset.blTiltBound) return;
    el.dataset.blTiltBound = 'true';
    el.classList.add('bl-tilt');

    const max = parseFloat(el.dataset.blTilt) || 8;
    let frame = null;

    const onMove = (event) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const rect = el.getBoundingClientRect();
        const px = (event.clientX - rect.left) / rect.width;
        const py = (event.clientY - rect.top) / rect.height;

        el.style.setProperty('--bl-ry', `${(px - 0.5) * max * 2}deg`);
        el.style.setProperty('--bl-rx', `${(0.5 - py) * max * 2}deg`);
        el.style.setProperty('--bl-px', `${px * 100}%`);
        el.style.setProperty('--bl-py', `${py * 100}%`);
      });
    };

    const reset = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = null;
      el.classList.remove('bl-tilt--active');
      el.style.removeProperty('--bl-rx');
      el.style.removeProperty('--bl-ry');
    };

    el.addEventListener('pointerenter', () => el.classList.add('bl-tilt--active'));
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', reset);
  });
}

/* --------------------------------------------------------------------------
   Magnetic buttons
   -------------------------------------------------------------------------- */

function initMagnetic(root = document) {
  if (prefersReducedMotion()) return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  root.querySelectorAll('[data-bl-magnetic]').forEach((el) => {
    if (el.dataset.blMagneticBound) return;
    el.dataset.blMagneticBound = 'true';
    el.classList.add('bl-magnetic');

    const strength = parseFloat(el.dataset.blMagnetic) || 0.25;

    el.addEventListener('pointermove', (event) => {
      const rect = el.getBoundingClientRect();
      const x = event.clientX - rect.left - rect.width / 2;
      const y = event.clientY - rect.top - rect.height / 2;
      el.style.setProperty('--bl-mx', `${x * strength}px`);
      el.style.setProperty('--bl-my', `${y * strength}px`);
    });

    el.addEventListener('pointerleave', () => {
      el.style.setProperty('--bl-mx', '0px');
      el.style.setProperty('--bl-my', '0px');
    });
  });
}

/* --------------------------------------------------------------------------
   Parallax — one scroll listener drives every registered layer
   -------------------------------------------------------------------------- */

const parallaxLayers = new Set();
let parallaxTicking = false;

function updateParallax() {
  parallaxTicking = false;
  const viewport = window.innerHeight;

  parallaxLayers.forEach((layer) => {
    const host = layer.parentElement;
    if (!host) return;

    const rect = host.getBoundingClientRect();
    if (rect.bottom < -200 || rect.top > viewport + 200) return;

    /* -1 when the section is just below the fold, +1 when just above it. */
    const progress = (viewport / 2 - (rect.top + rect.height / 2)) / (viewport / 2 + rect.height / 2);
    const depth = parseFloat(layer.dataset.blParallax) || 10;
    layer.style.transform = `translate3d(0, ${progress * depth}%, 0)`;
  });
}

function requestParallax() {
  if (parallaxTicking) return;
  parallaxTicking = true;
  requestAnimationFrame(updateParallax);
}

function initParallax(root = document) {
  if (prefersReducedMotion()) return;

  root.querySelectorAll('[data-bl-parallax]').forEach((layer) => {
    if (layer.dataset.blParallaxBound) return;
    layer.dataset.blParallaxBound = 'true';
    layer.style.willChange = 'transform';
    parallaxLayers.add(layer);
  });

  if (parallaxLayers.size > 0) requestParallax();
}

window.addEventListener('scroll', requestParallax, { passive: true });
window.addEventListener('resize', requestParallax, { passive: true });

/* --------------------------------------------------------------------------
   Bee flight — scroll scrubs the flight path

   The CSS keyframes stay the single source of truth for the path, the pace
   changes and the loop. In scroll mode they are held paused and this scrubs
   their currentTime from the scroll position, so the bee crosses the section
   as the visitor scrolls through it and stops when they stop. The target is
   eased towards on each frame so wheel steps glide instead of jumping.
   -------------------------------------------------------------------------- */

const beeFlights = new Set();
let beeTicking = false;

function beeTargetProgress(bee) {
  const host = bee.parentElement;
  if (!host) return 0;

  const rect = host.getBoundingClientRect();
  const viewport = window.innerHeight;
  const top = rect.top + window.scrollY;

  /* The flight starts the moment the section comes into view (or at the very
     top of the page when it is already there on load) and takes a fraction of
     the section's height to complete — enough to finish while it is still
     mostly on screen, since the section itself is scrolling away too. */
  const start = Math.max(0, top - viewport);
  const distance = Math.max(1, rect.height * ((parseFloat(bee.dataset.blBeeDistance) || 50) / 100));
  return Math.min(Math.max((window.scrollY - start) / distance, 0), 1);
}

function beeApply(bee, progress) {
  bee.querySelectorAll('.bl-bee__craft, .bl-bee__bank').forEach((part) => {
    part.getAnimations().forEach((animation) => {
      const duration = animation.effect?.getTiming().duration;
      if (typeof duration !== 'number' || !duration) return;
      animation.currentTime = progress * duration;
    });
  });
}

function updateBees() {
  beeTicking = false;
  let settling = false;

  beeFlights.forEach((bee) => {
    if (!bee.isConnected) {
      beeFlights.delete(bee);
      return;
    }

    const target = beeTargetProgress(bee);
    const current = bee.blProgress ?? target;
    const next = Math.abs(target - current) < 0.0005 ? target : current + (target - current) * 0.14;
    bee.blProgress = next;
    beeApply(bee, next);
    if (next !== target) settling = true;
  });

  /* Keep easing until every bee has caught up with the scroll position. */
  if (settling) requestBees();
}

function requestBees() {
  if (beeTicking) return;
  beeTicking = true;
  requestAnimationFrame(updateBees);
}

function initBeeFlight(root = document) {
  if (prefersReducedMotion()) return;

  root.querySelectorAll('.bl-bee--scroll').forEach((bee) => {
    if (bee.dataset.blBeeBound) return;
    bee.dataset.blBeeBound = 'true';
    beeFlights.add(bee);
  });

  if (beeFlights.size > 0) requestBees();
}

window.addEventListener('scroll', requestBees, { passive: true });
window.addEventListener('resize', requestBees, { passive: true });

/* --------------------------------------------------------------------------
   <bl-counter> — animated number count-up
   -------------------------------------------------------------------------- */

class BeelineCounter extends HTMLElement {
  connectedCallback() {
    this.target = parseFloat(this.dataset.value || this.textContent.replace(/[^\d.-]/g, '')) || 0;
    this.decimals = parseInt(this.dataset.decimals, 10) || 0;
    this.duration = parseInt(this.dataset.duration, 10) || 1800;
    /* Not `this.prefix` — Element.prefix is a read-only DOM property and
       assigning to it throws, which would break the whole counter. */
    this.prefixText = this.dataset.prefix || '';
    this.suffixText = this.dataset.suffix || '';

    if (prefersReducedMotion()) {
      this.render(this.target);
      return;
    }

    this.render(0);

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this.observer.disconnect();
        this.run();
      },
      { threshold: 0.4 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  render(value) {
    /* Formatted with Latin digits and thousands grouping regardless of the
       store language: the merchant types these values in Latin digits, and the
       rest of the storefront (prices, quantities) shows them the same way.
       Formatting an Arabic locale here would swap them for Arabic-Indic
       numerals mid-animation and no longer match what was entered. */
    this.textContent = `${this.prefixText}${value.toLocaleString('en-US', {
      minimumFractionDigits: this.decimals,
      maximumFractionDigits: this.decimals,
    })}${this.suffixText}`;
  }

  run() {
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / this.duration, 1);
      /* easeOutExpo — fast start, gentle settle */
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      this.render(this.target * eased);
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }
}

/* --------------------------------------------------------------------------
   <bl-quiz> — "find your honey" recommender
   Each step is a fieldset; each answer carries data-scores="handle:points,…".
   The highest-scoring result block is revealed at the end.
   -------------------------------------------------------------------------- */

class BeelineQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-bl-quiz-step]'));
    this.results = Array.from(this.querySelectorAll('[data-bl-quiz-result]'));
    this.progressBar = this.querySelector('[data-bl-quiz-progress]');
    this.progressLabel = this.querySelector('[data-bl-quiz-progress-label]');
    this.resultPanel = this.querySelector('[data-bl-quiz-panel]');
    this.backButton = this.querySelector('[data-bl-quiz-back]');
    this.restartButton = this.querySelector('[data-bl-quiz-restart]');

    if (this.steps.length === 0) return;

    this.scores = {};
    this.index = 0;
    this.history = [];

    this.addEventListener('click', this.onClick);
    this.backButton?.addEventListener('click', () => this.goBack());
    this.restartButton?.addEventListener('click', () => this.restart());

    this.showStep(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const answer = event.target.closest('[data-bl-quiz-answer]');
    if (!answer || !this.contains(answer)) return;

    event.preventDefault();
    this.history.push({ index: this.index, scores: { ...this.scores } });
    this.applyScores(answer.dataset.scores);

    if (this.index < this.steps.length - 1) {
      this.showStep(this.index + 1);
    } else {
      this.showResult();
    }
  };

  applyScores(raw) {
    if (!raw) return;
    raw.split(',').forEach((pair) => {
      const [key, points] = pair.split(':');
      if (!key) return;
      this.scores[key.trim()] = (this.scores[key.trim()] || 0) + (parseFloat(points) || 1);
    });
  }

  showStep(index) {
    this.index = index;
    this.resultPanel?.setAttribute('hidden', '');

    this.steps.forEach((step, i) => {
      step.hidden = i !== index;
    });

    if (this.backButton) this.backButton.hidden = index === 0;
    this.updateProgress((index / this.steps.length) * 100, index + 1);

    /* Move focus to the new question so keyboard and screen-reader users
       follow the flow instead of being left on a button that vanished. */
    const heading = this.steps[index].querySelector('[data-bl-quiz-question]');
    heading?.focus();
  }

  showResult() {
    const winner = Object.entries(this.scores).sort((a, b) => b[1] - a[1])[0]?.[0];

    this.steps.forEach((step) => {
      step.hidden = true;
    });

    let matched = false;
    this.results.forEach((result) => {
      const isMatch = result.dataset.blQuizResult === winner;
      result.hidden = !isMatch;
      if (isMatch) matched = true;
    });

    /* Never dead-end the visitor: fall back to the first result. */
    if (!matched && this.results[0]) this.results[0].hidden = false;

    if (this.backButton) this.backButton.hidden = false;
    this.resultPanel?.removeAttribute('hidden');
    this.updateProgress(100, this.steps.length);
    this.resultPanel?.querySelector('[data-bl-quiz-result-heading]')?.focus();
  }

  updateProgress(percent, current) {
    if (this.progressBar) {
      this.progressBar.style.setProperty('--bl-progress', `${percent}%`);
      this.progressBar.setAttribute('aria-valuenow', Math.round(percent));
    }
    if (this.progressLabel) {
      this.progressLabel.textContent = this.progressLabel.dataset.template
        ? this.progressLabel.dataset.template
            .replace('{{current}}', current)
            .replace('{{total}}', this.steps.length)
        : `${current} / ${this.steps.length}`;
    }
  }

  goBack() {
    const previous = this.history.pop();
    if (!previous) return;
    this.scores = previous.scores;
    this.showStep(previous.index);
  }

  restart() {
    this.scores = {};
    this.history = [];
    this.results.forEach((result) => {
      result.hidden = true;
    });
    this.showStep(0);
  }
}

/* --------------------------------------------------------------------------
   <bl-split-parallax> — sticky-stage scroll story

   The element is a tall track containing a sticky stage. As the track scrolls
   past, we write a single custom property, --bl-progress (0 → 1), on the host.
   Every moving part is a calc() off that value in CSS, so one property write
   per frame drives the whole scene and the main thread stays free.

   It only listens while the track is on screen, and does nothing at all under
   prefers-reduced-motion (the CSS then renders the finished composition).
   -------------------------------------------------------------------------- */

class BeelineSplitParallax extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-bl-split-stage]');
    if (!this.stage || prefersReducedMotion()) return;

    this.ticking = false;
    this.visible = false;

    this.onScroll = () => {
      if (this.ticking || !this.visible) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };

    /* Only bind the scroll listener while the section is actually on screen. */
    this.observer = new IntersectionObserver(
      (entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.update();
      },
      { rootMargin: '100px 0px' }
    );
    this.observer.observe(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  update() {
    const rect = this.getBoundingClientRect();
    /* Distance the track travels while the stage stays pinned. */
    const travel = rect.height - this.stage.offsetHeight;
    if (travel <= 0) return;

    const progress = Math.min(Math.max(-rect.top / travel, 0), 1);
    this.style.setProperty('--bl-progress', progress.toFixed(4));
  }
}

/* --------------------------------------------------------------------------
   <bl-tabs> — accessible tab switcher with roving focus
   -------------------------------------------------------------------------- */

class BeelineTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (this.tabs.length === 0) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    this.select(0);
  }

  onKeydown(event, index) {
    const isRtl = document.documentElement.dir === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';

    let next = null;
    if (event.key === forward) next = (index + 1) % this.tabs.length;
    else if (event.key === backward) next = (index - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;

    if (next === null) return;
    event.preventDefault();
    this.select(next);
    this.tabs[next].focus();
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }
}

/* --------------------------------------------------------------------------
   <bl-cards> — drag/snap card rail with prev/next and a live counter

   The rail is a native overflow-x scroller with scroll-snap, so touch and
   trackpad gestures work for free and keyboard users can tab through the
   cards. The buttons just scroll by one card width; nothing is re-parented or
   cloned, so the DOM order always matches the reading order.
   -------------------------------------------------------------------------- */

class BeelineCards extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-bl-cards-viewport]');
    this.prevButton = this.querySelector('[data-bl-cards-prev]');
    this.nextButton = this.querySelector('[data-bl-cards-next]');
    this.counter = this.querySelector('[data-bl-cards-counter]');
    this.cards = Array.from(this.querySelectorAll('[data-bl-card]'));

    if (!this.viewport || this.cards.length === 0) return;

    this.prevButton?.addEventListener('click', () => this.scrollByCard(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByCard(1));
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    this.update();
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  onScroll = () => {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  };

  /* Step by one card plus the gap between cards. */
  get step() {
    const [first, second] = this.cards;
    if (second) return Math.abs(second.offsetLeft - first.offsetLeft);
    return first.offsetWidth;
  }

  scrollByCard(direction) {
    /* scrollLeft runs negative under RTL in every current browser, so the
       logical direction has to be flipped to move the rail the way the
       arrow points. */
    const rtl = getComputedStyle(this).direction === 'rtl';
    this.viewport.scrollBy({ left: this.step * direction * (rtl ? -1 : 1), behavior: 'smooth' });
  }

  update() {
    const { scrollLeft, clientWidth, scrollWidth } = this.viewport;
    /* scrollLeft is negative under RTL, so compare on distance travelled. */
    const distance = Math.abs(scrollLeft);
    const max = scrollWidth - clientWidth;

    /* The rail does not rest at exactly 0: the viewport's inline padding and
       sub-pixel layout leave it a few px off at both ends, and the offset is
       larger under RTL. Allow a fraction of a card rather than a fixed 2px,
       or the "at start" state would never register. */
    const tolerance = Math.max(4, this.step * 0.08);
    const atStart = distance <= tolerance;
    const atEnd = distance >= max - tolerance;

    if (this.prevButton) this.prevButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd;

    if (this.counter) {
      const index = Math.min(Math.round(distance / this.step) + 1, this.cards.length);
      this.counter.textContent = this.counter.dataset.template
        ? this.counter.dataset.template
            .replace('{{current}}', index)
            .replace('{{total}}', this.cards.length)
        : `${index} / ${this.cards.length}`;
    }
  }
}

/* --------------------------------------------------------------------------
   <bl-copy> — copy a contact detail to the clipboard
   -------------------------------------------------------------------------- */

class BeelineCopy extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.feedback = this.querySelector('[data-bl-copy-feedback]');
    if (!this.button) return;

    /* The clipboard API needs a secure context; hide the affordance rather
       than offering a button that silently does nothing. */
    if (!navigator.clipboard) {
      this.button.hidden = true;
      return;
    }

    this.button.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(this.dataset.value || '');
        this.flash(this.dataset.copiedLabel || 'Copied');
      } catch {
        this.flash(this.dataset.errorLabel || 'Press Ctrl+C to copy');
      }
    });
  }

  flash(message) {
    if (!this.feedback) return;
    this.feedback.textContent = message;
    this.feedback.classList.add('bl-copy__feedback--visible');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.feedback.classList.remove('bl-copy__feedback--visible');
    }, 2200);
  }
}

/* --------------------------------------------------------------------------
   <bl-video> — cinematic video with play/pause and sound controls

   Autoplays muted when the section asks for it and motion is allowed,
   pauses while off screen (battery, bandwidth), and keeps the controls'
   state and labels in sync with the video whatever started or stopped it.
   -------------------------------------------------------------------------- */

class BeelineVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.toggle = this.querySelector('[data-bl-video-toggle]');
    this.bigPlay = this.querySelector('[data-bl-video-play]');
    this.sound = this.querySelector('[data-bl-video-sound]');
    this.userPaused = false;

    /* Reduced motion: never start on its own, wait for a tap. */
    this.wantsAutoplay = this.hasAttribute('autoplay') && !prefersReducedMotion();
    if (!this.wantsAutoplay) {
      this.video.removeAttribute('autoplay');
      this.video.pause();
    }

    this.onPlay = () => this.syncPlay(true);
    this.onPause = () => this.syncPlay(false);
    this.video.addEventListener('play', this.onPlay);
    this.video.addEventListener('pause', this.onPause);

    this.onToggle = () => {
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    };
    this.toggle?.addEventListener('click', this.onToggle);
    this.bigPlay?.addEventListener('click', this.onToggle);

    this.onSound = () => {
      this.video.muted = !this.video.muted;
      this.syncSound();
    };
    this.sound?.addEventListener('click', this.onSound);

    /* Run only while on screen. A pause caused here is not the visitor's
       choice, so it resumes when the section scrolls back into view. */
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (this.wantsAutoplay && !this.userPaused) this.play();
        } else if (!this.video.paused) {
          this.video.pause();
        }
      },
      { threshold: 0.2 }
    );
    this.observer.observe(this);

    this.syncPlay(!this.video.paused);
    this.syncSound();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.video?.removeEventListener('play', this.onPlay);
    this.video?.removeEventListener('pause', this.onPause);
  }

  play() {
    const attempt = this.video.play();
    /* Autoplay can be refused (no gesture yet); fall back to the play button. */
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => this.syncPlay(false));
  }

  syncPlay(playing) {
    this.classList.toggle('bl-video--playing', playing);
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
    this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
    this.toggle.querySelector('[data-bl-icon="play"]').hidden = playing;
    this.toggle.querySelector('[data-bl-icon="pause"]').hidden = !playing;
  }

  syncSound() {
    if (!this.sound) return;
    const muted = this.video.muted;
    this.sound.setAttribute('aria-pressed', muted ? 'false' : 'true');
    this.sound.setAttribute('aria-label', muted ? this.sound.dataset.labelUnmute : this.sound.dataset.labelMute);
    this.sound.querySelector('[data-bl-icon="sound-off"]').hidden = !muted;
    this.sound.querySelector('[data-bl-icon="sound-on"]').hidden = muted;
  }
}

/* --------------------------------------------------------------------------
   Cart helpers shared by the card stepper and the buy-now mirror

   The theme keeps its header count and cart drawer in sync through a
   `cart:change` event whose cart carries freshly rendered drawer sections.
   Reproducing that contract (bundled sections request, cart.js fetch, event
   with baseEvent) keeps everything updated without opening the drawer.
   -------------------------------------------------------------------------- */

const shopRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const cartCache = { promise: null };

function fetchCartJson(force = false) {
  if (!cartCache.promise || force) {
    const request = fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => {
      if (!r.ok) throw new Error(`cart.js ${r.status}`);
      return r.json();
    });
    /* A failed fetch must not poison the cache, or every later click would
       keep failing until a full reload. */
    request.catch(() => {
      if (cartCache.promise === request) cartCache.promise = null;
    });
    cartCache.promise = request;
  }
  return cartCache.promise;
}

/* Every cart:change — the theme's drawer, its product form, a card button —
   carries the newest cart, so it becomes the cache. Without this the cache
   kept a line the visitor had since removed in the drawer, the next + asked
   the server to update that vanished line, and nothing was added. */
document.addEventListener('cart:change', (event) => {
  const cart = event.detail && event.detail.cart;
  if (cart && Array.isArray(cart.items)) cartCache.promise = Promise.resolve(cart);
});

/* When did an add last open the cart drawer during this visit? Any
   variant:add — the theme's product form, a card button, a stepper — counts.
   The "first add, then after a pause" mode opens the drawer when nothing has
   opened it yet, and again only once the pause has passed since the last
   time; "first add of the visit" opens it exactly once. sessionStorage
   carries the time across pages; a blocked storage falls back to this page. */
const drawerOpened = { at: 0 };
const DRAWER_OPENED_KEY = 'beeline:cart-drawer-opened-at';

function cartDrawerLastOpenedAt() {
  let stored = 0;
  try {
    stored = parseInt(sessionStorage.getItem(DRAWER_OPENED_KEY), 10) || 0;
  } catch (error) {
    stored = 0;
  }
  return Math.max(drawerOpened.at, stored);
}

function cartDrawerShownThisVisit() {
  return cartDrawerLastOpenedAt() > 0;
}

document.addEventListener('variant:add', () => {
  drawerOpened.at = Date.now();
  try {
    sessionStorage.setItem(DRAWER_OPENED_KEY, String(drawerOpened.at));
  } catch (error) {
    /* storage unavailable: the in-memory time still covers this page */
  }
});

/* Back/forward cache restores and tab switches can bring back a page whose
   cart changed elsewhere in the meantime. */
function refreshCartFromServer() {
  return fetchCartJson(true).then((cart) => {
    BeelineCartStepper.instances.forEach((stepper) => {
      if (!stepper.busy) stepper.render(stepper.quantityIn(cart.items));
    });
    return cart;
  }).catch(() => {});
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted && BeelineCartStepper.instances.size > 0) refreshCartFromServer();
});

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && BeelineCartStepper.instances.size > 0) refreshCartFromServer();
});

function bundledSectionIds() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
  );
  return sections;
}

async function announceCart(cart, sections, baseEvent) {
  if (sections) cart.sections = sections;
  cartCache.promise = Promise.resolve(cart);
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent, cart } }));
}

/* --------------------------------------------------------------------------
   <bl-cart-stepper> — − / count / + on a product card

   + adds one of the variant to the cart, − removes one, and the count is
   how many the cart holds. Every stepper for the same variant stays in sync
   through the cart:change event, including changes made in the drawer.
   -------------------------------------------------------------------------- */

class BeelineCartStepper extends HTMLElement {
  static instances = new Set();

  connectedCallback() {
    this.variantId = parseInt(this.dataset.variantId, 10);
    this.minus = this.querySelector('[data-bl-stepper-minus]');
    this.plus = this.querySelector('[data-bl-stepper-plus]');
    this.count = this.querySelector('.bl-stepper__count');
    if (!this.variantId || !this.minus || !this.plus || !this.count) return;
    BeelineCartStepper.instances.add(this);

    /* `quantity` is what the server last confirmed; `pending` is what the
       visitor has clicked since. The pill always shows the sum, instantly. */
    this.quantity = 0;
    this.pending = 0;
    this.inflight = 0;
    this.busy = false;

    this.onMinus = () => this.change(-1);
    this.onPlus = () => this.change(1);
    this.minus.addEventListener('click', this.onMinus);
    this.plus.addEventListener('click', this.onPlus);

    this.onCartChange = (event) => {
      const cart = event.detail && event.detail.cart;
      if (cart && cart.items && !this.busy) this.render(this.quantityIn(cart.items));
    };
    this.onCartRefresh = () => {
      if (this.busy) return;
      fetchCartJson(true).then((cart) => this.render(this.quantityIn(cart.items))).catch(() => {});
    };
    document.addEventListener('cart:change', this.onCartChange);
    document.addEventListener('cart:refresh', this.onCartRefresh);

    fetchCartJson()
      .then((cart) => this.render(this.quantityIn(cart.items)))
      .catch(() => {});
  }

  disconnectedCallback() {
    BeelineCartStepper.instances.delete(this);
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('cart:refresh', this.onCartRefresh);
  }

  quantityIn(items) {
    return items.filter((item) => item.variant_id === this.variantId).reduce((sum, item) => sum + item.quantity, 0);
  }

  /* The one-time-purchase line for this variant, so a subscription line of
     the same product is never touched. */
  lineFor(items) {
    return (
      items.find((item) => item.variant_id === this.variantId && !item.selling_plan_allocation) ||
      items.find((item) => item.variant_id === this.variantId)
    );
  }

  /* Click: show the new number at once, then let the server catch up. Clicks
     made while a request is in flight accumulate and go out together. */
  change(delta) {
    if (this.hasAttribute('data-unavailable')) return;
    if (this.quantity + this.inflight + this.pending + delta < 0) return;
    this.pending += delta;
    this.paint();
    this.pop();
    if (!this.busy) this.flush();
  }

  async flush() {
    this.busy = true;
    this.setAttribute('aria-busy', 'true');

    try {
      while (this.pending !== 0) {
        const delta = this.pending;
        this.pending = 0;
        this.inflight = delta;
        try {
          const cart = await this.apply(delta);
          this.quantity = this.quantityIn(cart.items);
          this.inflight = 0;
        } catch (error) {
          /* Roll back to what the server last confirmed. */
          this.pending = 0;
          this.inflight = 0;
          this.paint();
          this.classList.add('bl-stepper--error');
          setTimeout(() => this.classList.remove('bl-stepper--error'), 500);
          this.dispatchEvent(
            new CustomEvent('cart:error', { bubbles: true, detail: { error: (error && (error.description || error.message)) || '' } })
          );
          /* Whatever went wrong, the count must end on the truth. */
          const cart = await fetchCartJson(true).catch(() => null);
          if (cart) this.quantity = this.quantityIn(cart.items);
        }
        this.paint();
      }
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  /* /cart/add.js followed by the cart it produced. Shopify merges the add
     into an existing line of the same variant, so this is safe to use as the
     fallback whenever the cached line turns out to be gone. */
  async add(delta, root, sections, headers, before = 0) {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ items: [{ id: this.variantId, quantity: delta }], sections: sections.join(','), sections_url: window.location.pathname })
    });
    const added = await response.json();
    if (!response.ok) throw added;
    const cart = await fetchCartJson(true);
    await this.announce(cart, added.sections, delta, before);
    return cart;
  }

  /* After a successful + the cart drawer can open, exactly as the theme's
     own add to cart does: variant:add makes the drawer show, then
     cart:change (with the drawer's freshly rendered sections) fills it in.
     data-open-drawer says when: "cooldown" (the first add, then again only
     once data-drawer-cooldown seconds have passed since the drawer last
     opened for an add), "first_visit" (the first add of the visit only),
     "first_product" (each time a product goes from 0 into the cart) or
     "every" add. Without the attribute, or on a −, only cart:change goes
     out. */
  async announce(cart, sections, delta, before = 0) {
    const mode = this.dataset.openDrawer;
    let opensDrawer = delta > 0 && !!mode && window.themeVariables?.settings?.cartType === 'drawer';
    if (opensDrawer && mode === 'first_product') opensDrawer = before === 0;
    if (opensDrawer && mode === 'first_visit') opensDrawer = !cartDrawerShownThisVisit();
    if (opensDrawer && mode === 'cooldown') {
      const seconds = parseFloat(this.dataset.drawerCooldown);
      const pause = (Number.isFinite(seconds) ? seconds : 15) * 1000;
      opensDrawer = Date.now() - cartDrawerLastOpenedAt() >= pause;
    }

    if (opensDrawer) {
      if (sections) cart.sections = sections;
      this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: [{ id: this.variantId, quantity: delta }], cart } }));
      await announceCart(cart, sections, 'variant:add');
      return;
    }

    await announceCart(cart, sections, delta > 0 ? 'bl-stepper:add' : 'bl-stepper:remove');
  }

  /* One request whenever possible: /cart/update.js sets the line's quantity
     and returns the whole cart, with the drawer's rendered sections, in the
     same round trip. Only the very first add of a product needs /cart/add.js
     (a line has to exist before it can be updated), followed by the cart. */
  async apply(delta) {
    const root = shopRoot();
    const sections = bundledSectionIds();
    const current = await fetchCartJson().catch(() => fetchCartJson(true));
    const line = this.lineFor(current.items);
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

    if (!line) {
      if (delta <= 0) return current;
      return this.add(delta, root, sections, headers, 0);
    }

    const before = this.quantityIn(current.items);
    const target = Math.max(0, line.quantity + delta);
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ updates: { [line.key]: target }, sections: sections.join(','), sections_url: window.location.pathname })
    });
    const cart = await response.json();
    if (!response.ok) throw cart;
    const rendered = cart.sections;
    delete cart.sections;

    /* The line we updated is not in the cart that came back: it had been
       removed elsewhere and the server ignored the update. Add afresh so the
       visitor's + still lands. */
    if (delta > 0 && target > 0 && !cart.items.some((item) => item.key === line.key)) {
      cartCache.promise = Promise.resolve(cart);
      return this.add(delta, root, sections, headers, 0);
    }

    await this.announce(cart, rendered, delta, before);
    return cart;
  }

  /* Server-confirmed quantity arrived from elsewhere (drawer, another card). */
  render(quantity) {
    this.quantity = quantity;
    this.paint();
  }

  paint() {
    const shown = Math.max(0, this.quantity + this.inflight + this.pending);
    this.count.textContent = String(shown);
    this.classList.toggle('bl-stepper--active', shown > 0);
    this.minus.disabled = shown <= 0;
  }

  pop() {
    this.count.classList.remove('bl-stepper__count--pop');
    void this.count.offsetWidth;
    this.count.classList.add('bl-stepper__count--pop');
  }
}

/* --------------------------------------------------------------------------
   <bl-buy-now> — add the product form's selection and go to checkout

   Used by the mirrored Buy buttons block on the product page, where the
   dynamic checkout button cannot be duplicated. Reads the real form (variant,
   quantity, properties) through its form attribute, adds it, then leaves.
   -------------------------------------------------------------------------- */

class BeelineBuyNow extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button, a');
    this.formId = this.getAttribute('form');
    if (!this.button || !this.formId) return;

    this.onClick = (event) => {
      event.preventDefault();
      this.buy();
    };
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  async buy() {
    const form = document.getElementById(this.formId);
    if (!form || this.button.hasAttribute('aria-busy')) return;
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    this.button.setAttribute('aria-busy', 'true');
    this.button.setAttribute('disabled', '');

    try {
      const data = new FormData(form);
      data.delete('sections');
      data.delete('sections_url');

      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error.description || error.message || '' } }));
        return;
      }

      window.location.assign(this.dataset.checkoutUrl || `${shopRoot()}checkout`);
    } finally {
      this.button.removeAttribute('aria-busy');
      this.button.removeAttribute('disabled');
    }
  }
}

/* --------------------------------------------------------------------------
   <bl-card-buy> — a product card's Add to cart / Buy now without a <form>

   Adds one of the variant through the Ajax API. With data-action="cart" it
   then updates the header and opens the cart drawer exactly like the theme's
   own add (bundled sections + variant:add + cart:change); with
   data-action="checkout" it goes straight to checkout.
   -------------------------------------------------------------------------- */

class BeelineCardBuy extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button, a');
    this.variantId = parseInt(this.dataset.variantId, 10);
    if (!this.button || !this.variantId) return;

    this.onClick = (event) => {
      event.preventDefault();
      this.buy();
    };
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  async buy() {
    if (this.button.hasAttribute('aria-busy')) return;
    this.button.setAttribute('aria-busy', 'true');
    this.button.setAttribute('disabled', '');

    try {
      const root = shopRoot();
      const checkout = this.dataset.action === 'checkout';
      const sections = checkout ? [] : bundledSectionIds();

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }], sections: sections.join(','), sections_url: window.location.pathname })
      });
      const added = await response.json();
      if (!response.ok) {
        this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: added.description || added.message || '' } }));
        return;
      }

      if (checkout) {
        window.location.assign(this.dataset.checkoutUrl || `${root}checkout`);
        return;
      }

      const cart = await fetchCartJson(true);
      cart.sections = added.sections;
      /* Same two events the theme's product form fires: the drawer opens on
         variant:add, everything else refreshes on cart:change. */
      this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: added.items || [added], cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    } finally {
      this.button.removeAttribute('aria-busy');
      this.button.removeAttribute('disabled');
    }
  }
}

/* --------------------------------------------------------------------------
   Boot
   -------------------------------------------------------------------------- */

function initAll(root = document) {
  initReveal(root);
  initTilt(root);
  initMagnetic(root);
  initParallax(root);
  initBeeFlight(root);
}

if (!customElements.get('bl-counter')) customElements.define('bl-counter', BeelineCounter);
if (!customElements.get('bl-split-parallax')) customElements.define('bl-split-parallax', BeelineSplitParallax);
if (!customElements.get('bl-quiz')) customElements.define('bl-quiz', BeelineQuiz);
if (!customElements.get('bl-tabs')) customElements.define('bl-tabs', BeelineTabs);
if (!customElements.get('bl-cards')) customElements.define('bl-cards', BeelineCards);
if (!customElements.get('bl-copy')) customElements.define('bl-copy', BeelineCopy);
if (!customElements.get('bl-video')) customElements.define('bl-video', BeelineVideo);
if (!customElements.get('bl-cart-stepper')) customElements.define('bl-cart-stepper', BeelineCartStepper);
if (!customElements.get('bl-card-buy')) customElements.define('bl-card-buy', BeelineCardBuy);
if (!customElements.get('bl-buy-now')) customElements.define('bl-buy-now', BeelineBuyNow);

initAll();

/* Re-scan when the theme editor swaps a section in, and when the merchant
   toggles reduced motion mid-session. */
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:section:select', (event) => initAll(event.target));

reducedMotion.addEventListener('change', () => {
  if (prefersReducedMotion()) {
    document.documentElement.classList.remove('bl-reveal-ready');
  } else {
    document.documentElement.classList.add('bl-reveal-ready');
    initAll();
  }
});
